import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import campService from '../services/campService';

const CampSelection = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const centerId = searchParams.get('centerId');
  const country = searchParams.get('country') || 'USA'; // Get country from URL or default to USA

  // Get center data from Redux
  const centerData = useSelector((state) => state.center);

  const [camps, setCamps] = useState([]);
  const [selectedCamp, setSelectedCamp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (centerId) {
      fetchCamps();
    }
  }, [centerId, country]);

  const fetchCamps = async () => {
    try {
      setLoading(true);
      const data = await campService.getCampsByCenter(centerId, country);
      setCamps(data || []);
      setError(null);
    } catch (err) {
      setError('Failed to load camps. Please try again later.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectCamp = (camp) => {
    setSelectedCamp(camp);
  };

  const handleProceedToRegistration = () => {
    if (selectedCamp) {
      // Navigate to login page instead of registration
      navigate(`/camp/login?campId=${selectedCamp.camp_Id}`);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 py-8">
      <div className="container-custom">
        {/* Header - Compact */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/camp/center')}
            className="inline-flex items-center text-blue-600 hover:text-blue-800 mb-3 font-semibold hover:translate-x-1 transition-transform text-sm"
          >
            ← Back to Centers
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent mb-2">
                Choose Your Camp
              </h1>
              {centerData?.center_Name && (
                <p className="text-sm text-gray-600">
                  📍 <span className="font-bold text-indigo-600">{centerData.center_Name}</span>
                </p>
              )}
            </div>
            {selectedCamp && (
              <button
                onClick={() => {}}
                className="text-gray-600 hover:text-gray-900 text-2xl"
                title="Close details"
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <div className="inline-block">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
            <p className="mt-4 text-gray-600">Loading camps...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Camps Grid - Tile Layout */}
        {!loading && camps.length > 0 && (
          <div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {camps.map((camp) => (
              <div
                key={camp.camp_Id}
                onClick={() => handleSelectCamp(camp)}
                className={`item-card cursor-pointer ${
                  selectedCamp?.camp_Id === camp.camp_Id
                    ? 'selected'
                    : ''
                }`}
              >
                  {/* Image Section */}
                  {camp.camp_Image_Url && (
                    <div className="h-56 bg-gradient-to-br from-indigo-400 to-purple-400 overflow-hidden relative">
                      <img
                        src={camp.camp_Image_Url?.trim()}
                        alt={camp.camp_Name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/400x300?text=Camp';
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </div>
                  )}

                  {/* Content Section */}
                  <div className="p-7">
                    <h3 className="text-xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent mb-3 line-clamp-2">
                      {camp.camp_Name}
                    </h3>

                    {/* Dates */}
                    <div className="mb-4 text-sm text-gray-600 font-semibold">
                      📅 {new Date(camp.camp_Date_From).toLocaleDateString()} - {new Date(camp.camp_Date_To).toLocaleDateString()}
                    </div>

                    {/* Fee */}
                    {camp.camp_Fee > 0 && (
                      <div className="mb-4 bg-gradient-to-r from-indigo-100 to-purple-100 p-3 rounded-lg">
                        <p className="text-2xl font-bold text-indigo-600">
                          ${camp.camp_Fee}
                        </p>
                        <p className="text-gray-600 text-xs">per person</p>
                      </div>
                    )}

                    {/* Contact Info */}
                    {camp.camp_PhoneNo && (
                      <div className="mb-3 text-sm text-gray-600 font-semibold">
                        📞 {camp.camp_PhoneNo}
                      </div>
                    )}

                    {/* Address */}
                    {camp.camp_Address && (
                      <div className="mb-4 text-sm text-gray-600 line-clamp-2 font-semibold">
                        📍 {camp.camp_Address}
                      </div>
                    )}

                    <button className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-bold py-3 px-4 rounded-xl transition transform hover:shadow-lg">
                      View Details ✨
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Selected Camp Details - Same Page */}
            {selectedCamp && (
              <div className="bg-gradient-to-br from-white via-indigo-50 to-purple-50 rounded-3xl shadow-2xl p-12 border-2 border-indigo-200">
                <div className="flex items-center justify-between mb-8">
                  <h2 className="text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">{selectedCamp.camp_Name}</h2>
                  <button onClick={() => handleSelectCamp(null)} className="text-gray-600 hover:text-gray-900 text-3xl font-bold">✕</button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                  {/* Left: Images */}
                  <div className="space-y-6">
                    {selectedCamp.camp_Image_Url && (
                      <div className="rounded-2xl overflow-hidden shadow-lg border-4 border-indigo-100">
                        <img src={selectedCamp.camp_Image_Url?.trim()} alt={selectedCamp.camp_Name} className="w-full h-80 object-cover" onError={(e) => { e.target.src = 'https://via.placeholder.com/400x300?text=Camp'; }} />
                      </div>
                    )}
                    {selectedCamp.camp_Flayer_Link && (
                      <div>
                        <p className="text-sm font-bold text-gray-600 mb-3 uppercase tracking-wide">Camp Flyer</p>
                        <div className="rounded-2xl overflow-hidden shadow-lg border-4 border-purple-100 mb-3">
                          <img src={selectedCamp.camp_Flayer_Link?.trim()} alt="Flyer" className="w-full h-auto" onError={(e) => { e.target.src = 'https://via.placeholder.com/400x300?text=Flyer'; }} />
                        </div>
                        <a href={selectedCamp.camp_Flayer_Link} target="_blank" rel="noopener noreferrer" className="w-full block text-center bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-bold py-3 px-6 rounded-xl">📄 Download Flyer</a>
                      </div>
                    )}
                  </div>

                  {/* Right: Details */}
                  <div className="space-y-6">
                    {/* Key Info */}
                    <div className="bg-white rounded-2xl p-6 border-2 border-gray-100 space-y-4">
                      {selectedCamp.camp_Date_From && <div><p className="text-gray-600 text-sm font-semibold mb-1">📅 Duration</p><p className="font-bold text-lg">{new Date(selectedCamp.camp_Date_From).toLocaleDateString()} - {new Date(selectedCamp.camp_Date_To).toLocaleDateString()}</p></div>}
                      {selectedCamp.camp_Fee > 0 && <div><p className="text-gray-600 text-sm font-semibold mb-1">💰 Fee</p><p className="font-bold text-2xl text-indigo-600">${selectedCamp.camp_Fee}</p></div>}
                    </div>

                    {/* Contact Information */}
                    <div className="bg-white rounded-2xl p-6 border-2 border-gray-100 space-y-4">
                      <p className="font-bold text-lg text-gray-900 flex items-center gap-2"><span>📞</span> Contact</p>
                      {selectedCamp.camp_PhoneNo && <div><p className="text-gray-600 text-sm font-semibold">Phone</p><p className="font-bold text-indigo-600">{selectedCamp.camp_PhoneNo}</p></div>}
                      {selectedCamp.camp_Email && <div><p className="text-gray-600 text-sm font-semibold">Email</p><p className="font-bold text-indigo-600">{selectedCamp.camp_Email}</p></div>}
                      {selectedCamp.camp_Address && <div><p className="text-gray-600 text-sm font-semibold">📍 Address</p><p className="font-bold text-gray-900">{selectedCamp.camp_Address}</p></div>}
                    </div>

                    {/* Register Button */}
                    <button onClick={handleProceedToRegistration} className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-bold py-5 px-8 rounded-2xl flex items-center justify-center gap-3 text-lg hover:shadow-xl transition">✨ Register Now →</button>

                    {/* Info Box */}
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-6"><p className="text-blue-900 font-semibold text-center">🎉 Limited spots available! Secure your spot now.</p></div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Empty State */}
        {!loading && camps.length === 0 && !error && (
          <div className="text-center py-12">
            <p className="text-gray-600 text-lg">No camps available for this center.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CampSelection;
